import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import {
  SmartDisplay as SmartDisplayIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
} from "@mui/icons-material";
import { useUserContext } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";
import { THEME_COLORS } from "../../context/constants";
import {
  getFeedbackLoopMedia,
  approveFeedbackLoopMedia,
  disapproveFeedbackLoopMedia,
} from "../../client/api";
import { FeedbackLoopMedia } from "../../context/types";
import useStyles from "./styles";

const POLL_INTERVAL = 5000;

const FeedbackLoop: React.FC = () => {
  const classes = useStyles();
  const { currentWorkspace } = useUserContext();
  const { addNotification } = useNotification();

  const [media, setMedia] = useState<FeedbackLoopMedia[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<FeedbackLoopMedia | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isDisapproving, setIsDisapproving] = useState(false);
  const [pendingDisapprove, setPendingDisapprove] = useState<FeedbackLoopMedia | null>(null);

  // Fetch media on mount + when workspace changes
  useEffect(() => {
    const fetchMedia = async () => {
      setIsLoading(true);
      try {
        const response = await getFeedbackLoopMedia(currentWorkspace?._id);
        if (response.success) {
          setMedia(response.data);
        }
      } catch (error) {
        console.error("Error fetching feedback loop media:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMedia();
  }, [currentWorkspace]);

  // Poll items still processing — re-fetch the list and merge updates
  useEffect(() => {
    const hasProcessing = media.some((m) => m.status === "processing");
    if (!hasProcessing) return;

    const intervalId = setInterval(async () => {
      try {
        const response = await getFeedbackLoopMedia(currentWorkspace?._id);
        if (response.success) {
          setMedia(response.data);
          setSelectedMedia((prev) =>
            prev ? response.data.find((m) => m._id === prev._id) ?? prev : prev
          );
        }
      } catch (error) {
        console.error("Error polling feedback loop media:", error);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [media, currentWorkspace]);

  const handleDownload = useCallback((item: FeedbackLoopMedia | null) => {
    if (!item || !item.mediaUrl) return;
    const ext = item.mediaType === "image" ? "jpg" : "mp4";
    const link = document.createElement("a");
    link.href = item.mediaUrl;
    link.download = `${item.engine}-${item._id}.${ext}`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleApprove = useCallback(
    async (item: FeedbackLoopMedia | null) => {
      if (!item) return;
      setIsApproving(true);
      try {
        const response = await approveFeedbackLoopMedia(item.engine, item._id);
        if (response.success) {
          setMedia((prev) =>
            prev.map((m) => (m._id === item._id ? { ...m, approved: true } : m))
          );
          setSelectedMedia((prev) =>
            prev?._id === item._id ? { ...prev, approved: true } : prev
          );
          addNotification("Media approved", "success");
        }
      } catch (error: any) {
        console.error("Error approving media:", error);
        addNotification(
          error.response?.data?.message || "Failed to approve media",
          "error"
        );
      } finally {
        setIsApproving(false);
      }
    },
    [addNotification]
  );

  const handleConfirmDisapprove = useCallback(async () => {
    const item = pendingDisapprove;
    if (!item) return;
    setIsDisapproving(true);
    try {
      const response = await disapproveFeedbackLoopMedia(item.engine, item._id);
      if (response.success) {
        setMedia((prev) => prev.filter((m) => m._id !== item._id));
        setSelectedMedia((prev) => (prev?._id === item._id ? null : prev));
        addNotification("Media removed", "success");
      }
    } catch (error: any) {
      console.error("Error disapproving media:", error);
      addNotification(
        error.response?.data?.message || "Failed to remove media",
        "error"
      );
    } finally {
      setIsDisapproving(false);
      setPendingDisapprove(null);
    }
  }, [pendingDisapprove, addNotification]);

  const truncatePrompt = (text: string, maxLen: number = 30) => {
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen) + "...";
  };

  const renderCardMedia = (item: FeedbackLoopMedia) => {
    if (item.status === "success" && item.mediaUrl) {
      if (item.mediaType === "image") {
        return (
          <img
            src={item.mediaUrl}
            alt={item.prompt}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        );
      }
      return (
        <video
          src={item.mediaUrl}
          muted
          preload="metadata"
          {...(item.thumbnailUrl ? { poster: item.thumbnailUrl } : {})}
        />
      );
    }
    if (item.status === "fail") {
      return (
        <Box className={classes.mediaCardOverlay}>
          <SmartDisplayIcon sx={{ fontSize: 28, color: "#e57373" }} />
          <Typography variant="caption" sx={{ color: "#e57373", fontSize: "10px" }}>
            Failed
          </Typography>
        </Box>
      );
    }
    return (
      <Box className={classes.mediaCardOverlay}>
        <CircularProgress size={24} sx={{ color: THEME_COLORS.yellow }} />
        <Typography variant="caption" sx={{ color: "#888", fontSize: "10px" }}>
          Processing...
        </Typography>
      </Box>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontFamily: "Lexend, sans-serif" }}
        >
          Feedback Loop
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#888", fontFamily: "Lexend, sans-serif", fontSize: "13px" }}
        >
          Review generated media. Approve to keep it, disapprove to delete it.
        </Typography>
      </div>

      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 6 }}>
          <CircularProgress size={22} sx={{ color: THEME_COLORS.yellow }} />
          <Typography variant="caption" sx={{ color: "#888" }}>
            Loading...
          </Typography>
        </Box>
      ) : media.length === 0 ? (
        <div className={classes.emptyState}>
          <div className={classes.emptyIcon}>
            <SmartDisplayIcon sx={{ fontSize: 36, color: "#ccc" }} />
          </div>
          <Typography
            variant="body1"
            sx={{ color: "#999", textAlign: "center", fontSize: "14px" }}
          >
            No media to review yet
          </Typography>
        </div>
      ) : (
        <div className={classes.grid}>
          {media.map((item) => (
            <div
              key={item._id}
              className={classes.mediaCard}
              onClick={() => setSelectedMedia(item)}
            >
              <span className={classes.mediaCardBadge}>
                {item.engine}
                {item.model ? ` · ${item.model}` : ""}
              </span>
              {item.approved === true && (
                <CheckCircleIcon
                  className={classes.approvedBadge}
                  sx={{ color: "#4caf50" }}
                />
              )}
              {renderCardMedia(item)}
              <div className={classes.mediaCardInfo}>
                <span className={classes.mediaCardPrompt}>
                  {truncatePrompt(item.prompt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      <Dialog
        open={Boolean(selectedMedia)}
        onClose={() => setSelectedMedia(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            fontFamily: "Lexend, sans-serif",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedMedia && (
            <>
              <Box className={classes.modalMediaArea}>
                {selectedMedia.status === "success" && selectedMedia.mediaUrl ? (
                  selectedMedia.mediaType === "image" ? (
                    <img
                      src={selectedMedia.mediaUrl}
                      alt={selectedMedia.prompt}
                      className={classes.modalVideo}
                      style={{ objectFit: "contain" }}
                    />
                  ) : (
                    <video
                      controls
                      autoPlay
                      src={selectedMedia.mediaUrl}
                      className={classes.modalVideo}
                    />
                  )
                ) : selectedMedia.status === "fail" ? (
                  <Box className={classes.modalStatusArea}>
                    <SmartDisplayIcon sx={{ fontSize: 48, color: "#e57373" }} />
                    <Typography variant="body1" sx={{ color: "#e57373", mt: 1 }}>
                      Generation failed
                    </Typography>
                  </Box>
                ) : (
                  <Box className={classes.modalStatusArea}>
                    <CircularProgress size={40} sx={{ color: THEME_COLORS.yellow }} />
                    <Typography variant="body1" sx={{ color: "#888", mt: 1.5 }}>
                      Processing...
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ px: 3, py: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", fontFamily: "Lexend, sans-serif" }}
                  >
                    {selectedMedia.engine}
                    {selectedMedia.model ? ` · ${selectedMedia.model}` : ""}
                  </Typography>
                  {selectedMedia.approved === true && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                      <Typography
                        variant="caption"
                        sx={{ color: "#4caf50", fontFamily: "Lexend, sans-serif" }}
                      >
                        Approved
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "13px",
                  }}
                >
                  {selectedMedia.prompt}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{ px: 3, pb: 2, pt: 0, gap: 1, justifyContent: "flex-start" }}
        >
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(selectedMedia)}
            disabled={selectedMedia?.status !== "success" || !selectedMedia?.mediaUrl}
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              borderRadius: "8px",
              borderColor: "#ddd",
              color: "#555",
              "&:hover": { borderColor: "#bbb", backgroundColor: "#fafafa" },
            }}
          >
            Download
          </Button>
          <Button
            startIcon={
              isApproving ? <CircularProgress size={16} /> : <ThumbUpIcon />
            }
            onClick={() => handleApprove(selectedMedia)}
            disabled={
              isApproving ||
              selectedMedia?.approved === true ||
              selectedMedia?.status !== "success"
            }
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              borderRadius: "8px",
              backgroundColor: "#4caf50",
              "&:hover": { backgroundColor: "#43a047" },
            }}
          >
            Approve
          </Button>
          <Button
            startIcon={<ThumbDownIcon />}
            onClick={() => selectedMedia && setPendingDisapprove(selectedMedia)}
            disabled={isDisapproving}
            variant="outlined"
            size="small"
            color="error"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              borderRadius: "8px",
            }}
          >
            Disapprove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disapprove confirmation */}
      <Dialog
        open={Boolean(pendingDisapprove)}
        onClose={() => !isDisapproving && setPendingDisapprove(null)}
        PaperProps={{ sx: { borderRadius: "16px", fontFamily: "Lexend, sans-serif" } }}
      >
        <DialogTitle sx={{ fontFamily: "Lexend, sans-serif" }}>
          Disapprove media?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#666", fontFamily: "Lexend, sans-serif" }}>
            This deletes the media. Continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPendingDisapprove(null)}
            disabled={isDisapproving}
            sx={{ textTransform: "none", fontFamily: "Lexend, sans-serif", color: "#555" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDisapprove}
            disabled={isDisapproving}
            variant="contained"
            color="error"
            startIcon={isDisapproving ? <CircularProgress size={16} /> : undefined}
            sx={{ textTransform: "none", fontFamily: "Lexend, sans-serif", borderRadius: "8px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FeedbackLoop;
